/** Pure math and seeded randomness shared by the spatial descriptor collector. */
export function readVec3(value: unknown, label: string): [
    number,
    number,
    number
] {
    if (!Array.isArray(value) || value.length !== 3 || !value.every(n => typeof n === 'number' && Number.isFinite(n))) {
        throw new Error(`${label} must be [x, y, z] — three finite numbers.`);
    }
    return [value[0], value[1], value[2]] as [
        number,
        number,
        number
    ];
}

export interface Vec3 {
    x: number;
    y: number;
    z: number;
}

export const SandboxVec3 = {
    add(a: Vec3, b: Vec3): Vec3 { return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z }; },
    subtract(a: Vec3, b: Vec3): Vec3 { return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }; },
    scale(v: Vec3, s: number): Vec3 { return { x: v.x * s, y: v.y * s, z: v.z * s }; },
    dot(a: Vec3, b: Vec3): number { return a.x * b.x + a.y * b.y + a.z * b.z; },
    cross(a: Vec3, b: Vec3): Vec3 {
        return { x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x };
    },
    length(v: Vec3): number { return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z); },
    normalize(v: Vec3): Vec3 {
        const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
        if (len === 0)
            return { x: 0, y: 0, z: 0 };
        return { x: v.x / len, y: v.y / len, z: v.z / len };
    },
    lerp(a: Vec3, b: Vec3, t: number): Vec3 {
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
    },
    distance(a: Vec3, b: Vec3): number {
        const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    },
    zero(): Vec3 { return { x: 0, y: 0, z: 0 }; },
    one(): Vec3 { return { x: 1, y: 1, z: 1 }; },
    up(): Vec3 { return { x: 0, y: 1, z: 0 }; },
    forward(): Vec3 { return { x: 0, y: 0, z: 1 }; },
    right(): Vec3 { return { x: 1, y: 0, z: 0 }; },
};

export const SandboxMathUtil = {
    clamp(value: number, min: number, max: number): number { return Math.max(min, Math.min(max, value)); },
    lerp(a: number, b: number, t: number): number { return a + (b - a) * t; },
    inverseLerp(a: number, b: number, value: number): number { return a === b ? 0 : (value - a) / (b - a); },
    remap(value: number, fromMin: number, fromMax: number, toMin: number, toMax: number): number {
        return toMin + (toMax - toMin) * ((value - fromMin) / (fromMax - fromMin));
    },
    degToRad(degrees: number): number { return degrees * Math.PI / 180; },
    radToDeg(radians: number): number { return radians * 180 / Math.PI; },
};

export function createRng(seed: number): () => number {
    let state = seed | 0 || 1;
    return () => {
        state ^= state << 13;
        state ^= state >> 17;
        state ^= state << 5;
        return (state >>> 0) / 4294967296;
    };
}
